import type { TranslateService } from '@ngx-translate/core';
import type { JssStyle } from 'jss';
import type { WebWidgetModel, WidgetMetaData } from '@web/types';

import { Widgets } from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import type { LegacyVideoDefault } from '@sites/entity-core';
import { createVideo } from '@sites/entity-core';

import { VideoViewComponent } from '@web/widgets/video/components/video-view/video-view.component';
import { VideoControlComponent } from '@web/widgets/video/components/video-control/video-control.component';

// DEFAULT COMPONENT SETTINGS — sourced from the canonical entity factory in @sites/entity-core.
//
// `data` is a function (not a static object) so `WebWidgetsFactoryService.prepareWidgetData`
// invokes it with `meta` — a video dropped into a zero block thus starts from the zero
// variant's defaults (currently byte-identical to the default variant's data, but routed
// through the variant-aware factory in @sites/entity-core for future-proofing).
const videoData = (_translate: TranslateService, meta?: WidgetMetaData): LegacyVideoDefault['data'] => {
    // The overloads on `createVideo` are picked per literal arg — calling each
    // branch separately keeps overload resolution happy (a `'default' | 'zero'`
    // union doesn't match either overload signature).
    const entity = meta?.type === 'zero' ? createVideo('zero') : createVideo('default');
    return entity.data as unknown as LegacyVideoDefault['data'];
};

const videoStyles: JssStyle = {
    width: '100%',
    margin: '0 auto',
    display: 'block',
};
// END DEFAULT SETTINGS

export const videoWidgetModel: WebWidgetModel = {
    componentView: VideoViewComponent,
    componentControl: VideoControlComponent,
    name: Widgets.VIDEO,
    label: 'widget_video_label',
    data: videoData,
    styles: videoStyles,
    permission: defaultWidgetPermission,
    // params: defaultWidgetParams,
    params: { ...defaultWidgetParams, type: ['default', 'zero'] },
    // Control + view components own the form via `WebLegacyWidgetSignalFormService`; the
    // resolver takes the minimal load path (just `widget` / `isSpecific` /
    // `isEduSite` — no `widget$`, no `container$`, no `controlForm`).
    signalForm: true,
};

export type VideoText = {
    label: string;
    placeholder: string;
};

export type { VideoPlayButton, VideoPlayButtonSize } from '@sites/entity-core';

// The play-button size→pixel map now lives next to the CSS-variable builder
// that consumes it (`@sites/entity-core`), so the
// styling layer stays the single source of truth. Re-exported here for
// discoverability from the widget model.
export { VIDEO_PLAY_BUTTON_SIZE_PX } from '@sites/entity-core/src/widget/video/styles/video.css-vars';
